import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, Package, User } from 'lucide-react';
import { GolfBagIcon } from '@golfauth/ui';
import { cn } from '../../lib/utils';
import { goToCore } from '../../lib/goToCore';
import { useProfileStrengthScore } from '../../hooks/useProfileStrengthScore';
import { arrivedFromCore } from '../../lib/arrivedFromCore';

const tabs = [
  { path: '/golfer/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/golfer/current-quotes', icon: FileText, label: 'Quotes' },
  { path: '/golfer/orders', icon: Package, label: 'Orders' },
  { path: '/golfer/profile', icon: User, label: 'Profile' },
];

export default function GolferBottomNav() {
  const location = useLocation();
  const profileStrengthScore = useProfileStrengthScore();
  const showProfileStrengthDot =
    profileStrengthScore !== undefined && profileStrengthScore < 85;

  // Publish the nav's rendered height (incl. its safe-area padding) as
  // --golfer-bottom-nav-h so fixed footers — the offer page's sticky
  // "Go to checkout" bar — sit above it instead of underneath (this nav is
  // z-50, so it was swallowing the checkout tap on phones). md:hidden makes
  // it 0 on desktop.
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const root = document.documentElement;
    const sync = () => root.style.setProperty('--golfer-bottom-nav-h', `${el.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener('resize', sync);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
      root.style.removeProperty('--golfer-bottom-nav-h');
    };
  }, []);

  return (
    <nav
      ref={navRef}
      className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-navy-500
                 border-t border-gray-200 dark:border-slate-700 z-50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-stretch">
        {/* My Bag — links to Core with session tokens.
            Suppressed when we arrived in Fitting from Core's Capacitor
            app (via SFSafariViewController popover): tapping My Bag
            would load Core *inside* the popover, on top of the parent
            Core app underneath. iOS doesn't expose a JS API to dismiss
            SFSafariViewController, so the user returns to Core via the
            popover's system "Done" button. Hiding the affordance here
            avoids the confusing infinite loop. */}
        {!arrivedFromCore() && (
          <button
            type="button"
            onClick={goToCore}
            className="flex-1 flex flex-col items-center justify-center py-2 gap-1
                       text-gray-600 dark:text-slate-400 hover:text-brand-600 dark:hover:text-brand-400
                       min-h-[52px]"
          >
            <GolfBagIcon className="w-5 h-5 object-contain" />
            <span className="text-[10px] font-medium">My Bag</span>
          </button>
        )}

        {tabs.map(({ path, icon: Icon, label }) => {
          const isActive = location.pathname === path;
          const isProfile = path === '/golfer/profile';
          return (
            <Link
              key={path}
              to={path}
              className={cn(
                'flex-1 flex flex-col items-center justify-center py-2 gap-1 min-h-[52px] relative',
                isActive
                  ? 'text-brand-600 dark:text-brand-400'
                  : 'text-gray-600 dark:text-slate-400 hover:text-brand-600 dark:hover:text-brand-400'
              )}
              aria-label={isProfile && showProfileStrengthDot ? 'Profile — complete your profile' : undefined}
            >
              <span className="relative inline-flex">
                <Icon className="w-5 h-5" />
                {isProfile && showProfileStrengthDot && (
                  <span className="absolute -top-0.5 -right-1 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-white dark:ring-navy-500" />
                )}
              </span>
              <span className="text-[10px] font-medium">{label}</span>
              {isActive && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-8 bg-brand-500 rounded-t-full" />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
